import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Checkbox, Tag, Popover } from 'antd';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeLoading, NdoeError, NdoeEmpty,
  NdoeRecherche, filtreTexte, NdoeImage, NdoeResponsiveTable, NdoeDataCard,
  useIsDesktop,
} from '../../components/ndoe';
import { messageErreur } from '../../components/ndoe/chargement';
import './qualificationAttributs.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

/*
 * QUALIFIER LES MODÈLES EN LOT — proposer, faire relire, puis écrire.
 *
 * POURQUOI CET ÉCRAN. Les 638 modèles existants ne portent aucun critère :
 * ouverture, maintien, bout, type de talon. Les saisir un par un serait
 * plusieurs heures de travail. Or la boutique les a DÉJÀ écrits — dans le nom
 * des modèles. « ESCARPINS OUVERTS DERRIERES », « SANDALE A CHEVILLE »…
 *
 * Le serveur en tire 172 propositions. Cet écran les montre, coche tout par
 * défaut, et laisse décocher ou corriger avant d'écrire quoi que ce soit.
 *
 * ⚠ RIEN N'EST ÉCRIT SANS UN CLIC EXPLICITE. C'est le principe de l'import de
 * listes : deviner est utile, décider à la place de quelqu'un ne l'est pas. Et
 * les règles de déduction se sont déjà trompées deux fois en développement —
 * « GROS TALON ROND » n'est pas un bout rond, « CROISE DEVANT » n'est pas une
 * ouverture. Elles ont été corrigées, mais la relecture reste le vrai garde-fou.
 */
export default function QualificationAttributs() {
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [attributs, setAttributs] = useState([]);
  const [propositions, setPropositions] = useState([]);
  const [retenues, setRetenues] = useState(() => new Set());
  const [corrections, setCorrections] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [q, setQ] = useState('');
  const [critere, setCritere] = useState(null);
  const [page, setPage] = useState(1);

  /*
    LE TABLEAU RESTE SUR ORDINATEUR, LA FICHE PREND LE RELAIS SUR TÉLÉPHONE.

    Constat de la propriétaire (21/08/2026) : la liste convient à l'écran du
    bureau mais pas au téléphone, où relire une ligne large revient à faire
    défiler de côté pour voir la valeur qu'on doit juger.

    On lit la largeur ICI, dans la page, et pas seulement dans
    NdoeResponsiveTable — parce que la PAGINATION diffère selon la
    présentation, et que ce composant ne peut pas le deviner (voir plus bas).
  */
  const estDesktop = useIsDesktop();
  const TAILLE_PAGE = 25;

  const cle = (p) => `${p.id_produit}|${p.code}`;

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/produit/attributs/propositions`, { params: { limite: 2000 } });
      const props = Array.isArray(data && data.propositions) ? data.propositions : [];
      setAttributs(Array.isArray(data && data.attributs) ? data.attributs : []);
      setPropositions(props);
      // TOUT COCHÉ AU DÉPART : les propositions sont majoritairement justes, et
      // décocher une exception est plus rapide que cocher 172 lignes. Le geste
      // par défaut doit être celui qu'on fait le plus souvent.
      setRetenues(new Set(props.map(cle)));
      setCorrections({});
    } catch (e) {
      setErreur(messageErreur(e, 'les propositions de qualification'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const vues = useMemo(() => propositions
    .filter(filtreTexte(q, ['nom_produit', 'code_produit', 'valeur', 'attribut']))
    .filter((p) => !critere || p.code === critere), [propositions, q, critere]);

  /*
    ⚠ PIÈGE MESURÉ DANS NdoeResponsiveTable (21/08/2026).

    Sur ordinateur, c'est la Table antd qui découpe `dataSource` en pages.
    Sur téléphone, NdoeMobileList rend TOUT ce qu'on lui donne, et PagerMobile
    s'efface si `pagination` ne porte pas de `total`. La configuration qui
    marchait sur ordinateur (`{ pageSize: 25 }`, sans `total`) affichait donc
    les 172 propositions en un seul rouleau, avec 172 sélecteurs à instancier
    — sur un téléphone d'entrée de gamme, ce n'est pas une nuance.

    On découpe donc NOUS-MÊMES pour le mobile, et on laisse antd faire son
    travail sur ordinateur. Passer la tranche aux deux aurait obligé à parier
    sur la façon dont antd traite un `total` qui ne correspond pas à la
    longueur de `dataSource` — un pari inutile quand la largeur est connue.
  */
  const pageVues = useMemo(
    () => vues.slice((page - 1) * TAILLE_PAGE, page * TAILLE_PAGE),
    [vues, page],
  );

  // Un filtre qui réduit la liste doit ramener à la première page : rester en
  // page 7 d'une liste qui n'en a plus que 2 donne un écran vide, qui se lit
  // comme « aucun résultat » alors qu'il y en a.
  useEffect(() => { setPage(1); }, [q, critere, propositions]);

  const basculer = (p) => setRetenues((s) => {
    const n = new Set(s);
    const k = cle(p);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const toutBasculer = (coche) => setRetenues((s) => {
    const n = new Set(s);
    // N'agit que sur ce qui est VISIBLE : sinon un filtre actif ferait cocher
    // des lignes qu'on ne regarde pas, ce qui est exactement ce qu'un écran de
    // relecture doit empêcher.
    vues.forEach((p) => { if (coche) n.add(cle(p)); else n.delete(cle(p)); });
    return n;
  });

  const appliquer = async () => {
    const lignes = propositions
      .filter((p) => retenues.has(cle(p)))
      .map((p) => ({ id_produit: p.id_produit, code: p.code, id_valeur: corrections[cle(p)] ?? p.id_valeur }));
    if (!lignes.length) { toast.info('Aucune ligne retenue.'); return; }
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/produit/attributs/appliquer`, { lignes });
      toast.success(`${data.appliques} critère(s) enregistré(s).`);
      await charger();
    } catch (e) {
      toast.error(messageErreur(e, 'l’enregistrement des critères'));
    } finally {
      setEnvoi(false);
    }
  };

  const colonnes = [
    {
      title: (
        <Checkbox
          checked={vues.length > 0 && vues.every((p) => retenues.has(cle(p)))}
          indeterminate={vues.some((p) => retenues.has(cle(p))) && !vues.every((p) => retenues.has(cle(p)))}
          onChange={(e) => toutBasculer(e.target.checked)}
        />
      ),
      key: 'coche',
      width: 48,
      render: (_, p) => <Checkbox checked={retenues.has(cle(p))} onChange={() => basculer(p)} />,
    },
    {
      /*
        LA PHOTO — c'est elle qui rend la relecture possible.

        Juger « ESCARPIN OUVERT DERRIERE VERNIS NOIR » sur son intitulé, c'est
        deviner. La vignette rend la décision immédiate.

        DEUX GESTES, parce que deux matériels : le SURVOL agrandit (souris),
        le CLIC ouvre en plein écran (tablette, où le survol n’existe pas).
        Ne garder que le survol aurait rendu la photo inutile au comptoir.
      */
      title: 'Photo',
      key: 'photo',
      width: 64,
      render: (_, p) => (p.img ? (
        <Popover
          placement="right"
          mouseEnterDelay={0.15}
          content={(
            <div className="qa-zoom">
              {/* `hero` et non `xl` : cette taille N'EXISTE PAS dans NdoeImage, et le
                  composant retombait SILENCIEUSEMENT sur `md` (64 px) — le zoom
                  faisait donc à peine plus que la vignette. Trouvé à l'œil. */}
              <NdoeImage src={p.img} alt={p.nom_produit} size="hero" preview={false} />
              <span className="qa-zoom__nom">{p.nom_produit}</span>
            </div>
          )}
        >
          <span className="qa-vignette">
            <NdoeImage src={p.img} alt={p.nom_produit} size="sm" />
          </span>
        </Popover>
      ) : (
        // Sans photo, on le DIT : une case vide se lirait comme un défaut
        // d'affichage, alors que c'est une fiche à compléter.
        <span className="qa-sansphoto" title="Ce modèle n’a pas de photo">—</span>
      )),
    },
    {
      title: 'Modèle',
      dataIndex: 'nom_produit',
      key: 'nom',
      render: (t, p) => (
        <span className="qa-modele">
          <span className="qa-nom">{t}</span>
          {/* La RÉFÉRENCE sous le nom : c’est ce qui est écrit sur la boîte,
              donc le seul repère pour aller vérifier la paire en rayon. */}
          {p.code_produit && <span className="qa-ref num">{p.code_produit}</span>}
        </span>
      ),
    },
    { title: 'Critère', dataIndex: 'attribut', key: 'attribut', width: 170, render: (t) => <Tag>{t}</Tag> },
    {
      title: 'Valeur proposée',
      key: 'valeur',
      width: 230,
      // CORRIGEABLE SUR PLACE : si la déduction se trompe, on ne veut pas
      // décocher puis rouvrir la fiche — on choisit la bonne valeur ici.
      render: (_, p) => {
        const attr = attributs.find((a) => a.code === p.code);
        return (
          <Select
            size="small" style={{ width: '100%' }}
            value={corrections[cle(p)] ?? p.id_valeur}
            onChange={(v) => setCorrections((m) => ({ ...m, [cle(p)]: v }))}
            options={(attr?.valeurs || []).map((v) => ({ value: v.id_valeur, label: v.libelle }))}
          />
        );
      },
    },
  ];

  /*
    LA FICHE MOBILE — un carré par proposition, avec TOUT ce qu'il faut pour
    trancher sans faire défiler de côté.

    Demande de la propriétaire (21/08/2026) : « affiche sous forme de fiche
    un peu comme la liste des commandes ». On reprend donc exactement le même
    objet — NdoeDataCard — que la liste des commandes, les clientes et les
    livraisons : photo, nom, référence, puis la valeur et la décision.

    CE QUI CHANGE PAR RAPPORT À LA FICHE COMMANDE, ET POURQUOI. La fiche
    commande s'ouvre au toucher. Ici, un toucher DÉCIDE : retenir ou écarter
    une valeur qui sera écrite en base. Rendre le carré entier basculant
    aurait fait qu'un doigt qui ripe pendant le défilement écarte une
    proposition SANS QUE PERSONNE NE LE VOIE. Sur un écran dont la raison
    d'être est la relecture, c'est le défaut à ne pas introduire. La décision
    passe donc par une case explicite, large, qui DIT son état.
  */
  const carteProposition = (p) => {
    const k = cle(p);
    const retenue = retenues.has(k);
    const attr = attributs.find((a) => a.code === p.code);
    return (
      <NdoeDataCard
        className={`qa-carte ${retenue ? 'is-retenue' : 'is-ecartee'}`}
        media={p.img
          ? <NdoeImage src={p.img} alt={p.nom_produit} size="lg" />
          : (
            // Pas de photo : on le DIT dans le carré. Un emplacement vide se
            // lirait comme une image qui n'a pas chargé.
            <span className="qa-carte__sansphoto">Sans photo</span>
          )}
        title={p.nom_produit}
        subtitle={p.code_produit || 'Référence non renseignée'}
        /*
          PAS DE BADGE ICI, ET C'EST MESURÉ. Le critère y tenait d'abord —
          il est insécable et fait 127 px : posé à côté du titre dans un
          en-tête en `space-between`, il ne laissait plus que 62 px au NOM
          DU MODÈLE — mesuré : 62 px de large pour 98 de haut, soit cinq
          lignes pour un nom de quatre mots. Autrement dit, la seule
          chose qu'on doit lire devenait la plus étroite de la fiche.

          Le critère est descendu en étiquette du sélecteur, où il dit à quoi
          répond la valeur affichée — sa vraie place.
        */
        actions={(
          /* Une case antd porte SON PROPRE <label> : le texte passe donc en
             enfant, jamais dans un <label> autour — deux labels imbriqués se
             renvoient le clic et la case ne bascule plus. */
          <Checkbox className="qa-carte__decision" checked={retenue} onChange={() => basculer(p)}>
            {retenue ? 'Retenue — sera enregistrée' : 'Écartée — ne sera pas enregistrée'}
          </Checkbox>
        )}
      >
        <div className="qa-carte__valeur">
          <span className="qa-carte__etiquette">{p.attribut}</span>
          <Select
            size="large"
            style={{ width: '100%' }}
            value={corrections[k] ?? p.id_valeur}
            onChange={(v) => setCorrections((m) => ({ ...m, [k]: v }))}
            options={(attr?.valeurs || []).map((v) => ({ value: v.id_valeur, label: v.libelle }))}
          />
        </div>
      </NdoeDataCard>
    );
  };

  const nbRetenues = propositions.filter((p) => retenues.has(cle(p))).length;
  // Sur téléphone il n'y a plus d'en-tête de tableau, donc plus de case
  // « tout cocher ». Le bouton du bas la remplace, en disant ce qu'il fera.
  const toutesCochees = vues.length > 0 && vues.every((p) => retenues.has(cle(p)));

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Catalogue"
        title="Qualifier les modèles"
        subtitle="Ce que le nom des modèles laisse deviner — à relire avant d’enregistrer"
        actions={<NdoeActionButton variant="secondary" onClick={charger}>Recalculer</NdoeActionButton>}
      />

      {chargement ? <NdoeLoading rows={6} />
        : erreur ? <NdoeError title="Les propositions n’ont pas pu être chargées" text={erreur} onRetry={charger} />
          : !propositions.length ? (
            <NdoeEmpty
              icon="✅"
              title="Rien à proposer"
              text="Aucun modèle non qualifié ne porte d’indice dans son nom. Les critères se saisissent alors à la création, ou depuis la fiche du modèle."
            />
          ) : (
            <>
              <p className="qa-intro">
                Ces valeurs sont <b>déduites du nom</b> de chaque modèle : rien n’est encore
                enregistré. Décochez ce qui vous semble faux, corrigez une valeur si besoin,
                puis validez.
              </p>

              <NdoeRecherche
                q={q}
                onQ={setQ}
                placeholder="Chercher un modèle, une référence, un critère, une valeur…"
                facettes={[{
                  cle: 'critere',
                  placeholder: 'Tous les critères',
                  options: attributs.map((a) => ({ value: a.code, label: a.libelle })),
                  valeur: critere,
                  onChange: setCritere,
                  largeur: 200,
                }]}
                actifs={(q ? 1 : 0) + (critere ? 1 : 0)}
                onEffacer={() => { setQ(''); setCritere(null); }}
                resume={<><b>{vues.length}</b> proposition{vues.length > 1 ? 's' : ''} · <b>{nbRetenues}</b> retenue{nbRetenues > 1 ? 's' : ''}</>}
              />

              <NdoeResponsiveTable
                rowKey={(p) => cle(p)}
                columns={colonnes}
                /* Ordinateur : antd découpe `vues` lui-même, exactement comme
                   avant. Téléphone : on lui passe la tranche déjà découpée.
                   Voir la note sur `pageVues`. */
                data={estDesktop ? vues : pageVues}
                renderCard={carteProposition}
                size="small"
                pagination={estDesktop
                  ? { pageSize: TAILLE_PAGE, showSizeChanger: false }
                  : {
                    current: page,
                    pageSize: TAILLE_PAGE,
                    total: vues.length,
                    showSizeChanger: false,
                    onChange: setPage,
                    showTotal: (t) => `${t} proposition${t > 1 ? 's' : ''} à relire`,
                  }}
                scroll={{ x: 'max-content' }}
                empty={{
                  icon: '🔍',
                  title: 'Aucune proposition',
                  text: 'Aucune ne correspond à cette recherche. Effacez les filtres pour tout revoir.',
                }}
              />

              <div className="ndoe-sticky-cta">
                <NdoeActionButton variant="secondary" onClick={() => toutBasculer(!toutesCochees)}>
                  {toutesCochees ? 'Tout décocher' : 'Tout cocher'}
                </NdoeActionButton>
                <NdoeActionButton variant="primary" loading={envoi} disabled={!nbRetenues} onClick={appliquer}>
                  Enregistrer {nbRetenues} critère{nbRetenues > 1 ? 's' : ''}
                </NdoeActionButton>
              </div>
            </>
          )}
    </NdoePage>
  );
}
